import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

const supportedTags = [
  "style",
  "script",
  "link",
  "title",
  "base",
  "meta",
  "noscript",
];

const getOwnerDocument = (element: HTMLElement | null) =>
  element?.ownerDocument ?? document;

export const useHeadContent = (head?: string) => {
  const componentId = useRef(uuidv4());
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!head || !container) return undefined;

    const ownerDocument = getOwnerDocument(container);
    if (!ownerDocument) return;

    const parser = new DOMParser();
    const headDoc = parser.parseFromString(head, "text/html");
    const createdElements: HTMLElement[] = [];

    headDoc.head.childNodes.forEach((node, index) => {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        supportedTags.includes(node.nodeName.toLowerCase())
      ) {
        const element = node as HTMLElement;
        const newElement = ownerDocument.createElement(element.tagName);

        Array.from(element.attributes).forEach(attr => {
          newElement.setAttribute(attr.name, attr.value);
        });

        if (element.innerHTML) {
          newElement.innerHTML = element.innerHTML;
        }

        newElement.setAttribute("data-custom-html-id", componentId.current);
        newElement.setAttribute("data-custom-html-index", index.toString());
        ownerDocument.head.appendChild(newElement);
        createdElements.push(newElement);
      }
    });

    return () => {
      createdElements.forEach(el => {
        if (ownerDocument.head.contains(el)) {
          ownerDocument.head.removeChild(el);
        }
      });
    };
  }, [head, container]);

  return setContainer;
};
